import { useCallback, useRef, useState } from 'react';

import {
    ENTITY_PILL_CLASSES,
    type EntityType,
} from '@apps/web/components/phoebe_chat/entity_visuals';
import { type MentionDropdownHandle } from '@apps/web/components/phoebe_chat/mention_dropdown';

export {
    ENTITY_PILL_CLASSES,
    type EntityType,
} from '@apps/web/components/phoebe_chat/entity_visuals';

// Lucide SVG paths for pill icons (14x14, matching size-3.5)
const ENTITY_ICON_SVG: Record<EntityType, string> = {
    caregiver:
        '<path d="M11 2v2"/><path d="M5 2v2"/><path d="M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1"/><path d="M8 15a6 6 0 0 0 12 0v-3"/><circle cx="20" cy="10" r="2"/>',
    client: '<circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/>',
    shift: '<path d="M16 14v2.2l1.6 1"/><path d="M16 2v4"/><path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5"/><path d="M3 10h5"/><path d="M8 2v4"/><circle cx="16" cy="16" r="6"/>',
    callout:
        '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
    playbook:
        '<path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/>',
};

function createPillIcon(type: string): SVGSVGElement | null {
    const paths = ENTITY_ICON_SVG[type as EntityType];
    if (!paths) {
        return null;
    }
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.style.flexShrink = '0';
    svg.innerHTML = paths;
    return svg;
}

// Build a contentEditable mention pill span (icon + name, carrying the
// [[type:id]] dataset that serialization reads back).
function createMentionPill(
    type: string,
    id: string,
    displayName: string,
): HTMLSpanElement {
    const pill = document.createElement('span');
    pill.contentEditable = 'false';
    pill.dataset['refType'] = type;
    pill.dataset['refId'] = id;
    pill.dataset['refName'] = displayName;
    pill.className =
        'inline-flex items-center gap-0.5 rounded ' +
        'px-1 ' +
        'text-[13px] font-medium select-none ' +
        'align-middle ' +
        (ENTITY_PILL_CLASSES[type as EntityType] ?? ENTITY_PILL_CLASSES.shift);
    const icon = createPillIcon(type);
    if (icon) {
        pill.appendChild(icon);
    }
    pill.appendChild(document.createTextNode(displayName));
    return pill;
}

/**
 * Replace the given DOM range with a mention pill followed by a trailing
 * space, and place the caret after the space. Shared by the `@`-picker
 * (deletes the `@query` before the caret) and the composer's name-catch hint
 * (deletes a typed-out name), so both produce identical pills.
 */
export function replaceRangeWithMentionPill(
    range: Range,
    type: string,
    id: string,
    displayName: string,
): void {
    const { startContainer, startOffset } = range;
    range.deleteContents();

    const pill = createMentionPill(type, id, displayName);
    const insertRange = document.createRange();
    insertRange.setStart(startContainer, startOffset);
    insertRange.collapse(true);
    insertRange.insertNode(pill);

    const space = document.createTextNode('\u00A0');
    pill.after(space);

    const selection = window.getSelection();
    if (selection) {
        const newRange = document.createRange();
        newRange.setStartAfter(space);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
    }
}

type MentionState = {
    readonly isOpen: boolean;
    readonly query: string;
    readonly triggerIndex: number;
};

const CLOSED_STATE: MentionState = {
    isOpen: false,
    query: '',
    triggerIndex: -1,
};

/**
 * Detects whether the character at `index` in `text` is a valid
 * @-mention trigger: it must be at the start of the string or
 * preceded by whitespace.
 */
function isValidTrigger(text: string, index: number): boolean {
    return index === 0 || /\s/.test(text[index - 1] ?? '');
}

// Two spaces covers "First Last" and "First Middle Last"; a third reads
// as a sentence, not a name.
const MAX_QUERY_SPACES = 2;
// Object replacement char standing in for pills in the scanned text.
const PILL_PLACEHOLDER = '\uFFFC';

/**
 * Scans backwards from `cursorPos` to find the nearest unmatched
 * `@` trigger. Returns the index of `@` or -1 if none found.
 * Crosses up to MAX_QUERY_SPACES whitespace chars so multi-word names
 * work; stops at line breaks and pills; a query starting with whitespace
 * (`@ jane`, `email @ x`) is not a mention attempt.
 */
export function findTriggerIndex(text: string, cursorPos: number): number {
    let spaceCount = 0;
    for (let i = cursorPos - 1; i >= 0; i--) {
        const ch = text[i];
        if (ch === undefined || ch === '\n' || ch === PILL_PLACEHOLDER) {
            return -1;
        }
        if (ch === '@' && isValidTrigger(text, i)) {
            const afterTrigger = text[i + 1];
            if (afterTrigger !== undefined && /\s/.test(afterTrigger)) {
                return -1;
            }
            return i;
        }
        if (/\s/.test(ch)) {
            spaceCount += 1;
            if (spaceCount > MAX_QUERY_SPACES) {
                return -1;
            }
        }
    }
    return -1;
}

export type EditorReference = {
    readonly type: string;
    readonly id: string;
    readonly display_name: string;
};

export type EditorDraftReference = {
    readonly type: string;
    readonly id: string;
    readonly displayName: string;
};

/**
 * Replaces the editor with text plus real entity pills for any matching
 * serialized references. Unknown tokens remain plain text so context is never
 * silently discarded.
 */
export function replaceEditorContent(
    editor: HTMLDivElement,
    text: string,
    references: ReadonlyArray<EditorDraftReference>,
): void {
    const referenceByToken = new Map(
        references.map((reference) => [
            `[[${reference.type}:${reference.id}]]`,
            reference,
        ]),
    );
    const tokenPattern = /\[\[[^:\]]+:[^\]]+\]\]/g;
    editor.innerHTML = '';

    for (const line of text.split('\n')) {
        if (editor.childNodes.length > 0) {
            editor.appendChild(document.createElement('br'));
        }
        let cursor = 0;
        for (const match of line.matchAll(tokenPattern)) {
            const token = match[0];
            const index = match.index;
            if (index > cursor) {
                editor.appendChild(
                    document.createTextNode(line.slice(cursor, index)),
                );
            }
            const reference = referenceByToken.get(token);
            editor.appendChild(
                reference
                    ? createMentionPill(
                          reference.type,
                          reference.id,
                          reference.displayName,
                      )
                    : document.createTextNode(token),
            );
            cursor = index + token.length;
        }
        if (cursor < line.length) {
            editor.appendChild(document.createTextNode(line.slice(cursor)));
        }
    }
}

/**
 * Collects display info from the mention pills currently in the editor so
 * optimistic/pending UI can render named badges before the server
 * persists resolved references.
 */
export function extractEditorReferences(
    editor: HTMLDivElement,
): Array<EditorReference> {
    const references: Array<EditorReference> = [];
    for (const node of editor.childNodes) {
        if (node instanceof HTMLElement) {
            const refType = node.dataset['refType'];
            const refId = node.dataset['refId'];
            const refName = node.dataset['refName'];
            if (refType && refId && refName) {
                references.push({
                    type: refType,
                    id: refId,
                    display_name: refName,
                });
            }
        }
    }
    return references;
}

/**
 * Reads the text content of a contentEditable div, converting
 * pill spans back to `[[type:id]]` tokens.
 */
export function serializeEditorContent(editor: HTMLDivElement): string {
    let result = '';
    for (const node of editor.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent ?? '';
        } else if (node instanceof HTMLElement) {
            const refType = node.dataset['refType'];
            const refId = node.dataset['refId'];
            if (refType && refId) {
                result += `[[${refType}:${refId}]]`;
            } else if (node.tagName === 'BR') {
                result += '\n';
            } else {
                result += node.textContent ?? '';
            }
        }
    }
    return result;
}

/**
 * Reads the editor as visible plain text: pill spans contribute their display
 * name and `<br>` line breaks become `\n`. Unlike the DOM's raw `textContent`
 * (which drops `<br>` entirely, fusing lines together), this preserves line
 * boundaries so text-matching over the draft can honor them.
 */
export function editorPlainText(editor: HTMLDivElement): string {
    let result = '';
    for (const node of editor.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent ?? '';
        } else if (node instanceof HTMLElement && node.tagName === 'BR') {
            result += '\n';
        } else {
            result += node.textContent ?? '';
        }
    }
    return result;
}

/**
 * Builds a text string from the editor content, replacing pill
 * spans with a single placeholder character that cannot be
 * mistaken for an `@` trigger. Returns the text and the cursor
 * offset within it.
 */
function getEditableTextAndCursor(editor: HTMLDivElement): {
    text: string;
    cursorPos: number;
} {
    const selection = window.getSelection();
    const cursorNode =
        selection && selection.rangeCount > 0
            ? selection.getRangeAt(0).startContainer
            : null;
    const cursorNodeOffset =
        selection && selection.rangeCount > 0
            ? selection.getRangeAt(0).startOffset
            : 0;

    let text = '';
    let cursorPos = -1;

    for (const node of editor.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            const content = node.textContent ?? '';
            if (node === cursorNode) {
                cursorPos = text.length + cursorNodeOffset;
            }
            text += content;
        } else if (node instanceof HTMLElement) {
            // Pill spans or other elements — treat as
            // opaque single character so the trigger
            // scanner skips over them.
            if (node.dataset['refType']) {
                if (cursorNode && node.contains(cursorNode)) {
                    cursorPos = text.length + 1;
                }
                text += PILL_PLACEHOLDER;
            } else if (node.tagName === 'BR') {
                if (node === cursorNode) {
                    cursorPos = text.length;
                }
                text += '\n';
            } else {
                const content = node.textContent ?? '';
                if (cursorNode && node.contains(cursorNode)) {
                    cursorPos = text.length;
                }
                text += content;
            }
        }
    }

    // If cursor is after all children
    if (cursorPos === -1 && cursorNode === editor) {
        cursorPos = text.length;
    }

    return { text, cursorPos };
}

type UseMentionDropdownArgs = {
    readonly editorRef: React.RefObject<HTMLDivElement | null>;
    readonly onContentChange: (serialized: string) => void;
    readonly enabled?: boolean;
};

export type ReferenceDisplayInfo = {
    readonly type: string;
    readonly displayName: string;
};

type UseMentionDropdownReturn = {
    readonly isOpen: boolean;
    readonly query: string;
    readonly handleInput: () => void;
    readonly handleKeyDown: (
        event: React.KeyboardEvent<HTMLDivElement>,
    ) => boolean;
    readonly insertReference: (
        type: string,
        id: string,
        displayName: string,
    ) => void;
    readonly dismiss: () => void;
    readonly clearEditor: () => void;
    readonly dropdownRef: React.RefObject<MentionDropdownHandle | null>;
};

export function useMentionDropdown({
    editorRef,
    onContentChange,
    enabled = false,
}: UseMentionDropdownArgs): UseMentionDropdownReturn {
    const [state, setState] = useState<MentionState>(CLOSED_STATE);
    const dropdownRef = useRef<MentionDropdownHandle | null>(null);

    const detectMention = useCallback(
        (text: string, cursorPos: number) => {
            if (!enabled) {
                return;
            }
            const idx = findTriggerIndex(text, cursorPos);
            if (idx === -1) {
                setState(CLOSED_STATE);
                return;
            }
            const query = text.slice(idx + 1, cursorPos);
            if (query.includes(']')) {
                setState(CLOSED_STATE);
                return;
            }
            setState({
                isOpen: true,
                query,
                triggerIndex: idx,
            });
        },
        [enabled],
    );

    const handleInput = useCallback(() => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        const serialized = serializeEditorContent(editor);
        onContentChange(serialized);

        const { text, cursorPos } = getEditableTextAndCursor(editor);
        detectMention(text, cursorPos);
    }, [editorRef, onContentChange, detectMention]);

    const dismiss = useCallback(() => {
        setState(CLOSED_STATE);
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>): boolean => {
            if (!state.isOpen) {
                return false;
            }

            switch (event.key) {
                case 'Escape': {
                    event.preventDefault();
                    dismiss();
                    return true;
                }
                case 'Enter': {
                    if (!event.shiftKey) {
                        event.preventDefault();
                        dropdownRef.current?.confirmSelection();
                        return true;
                    }
                    return false;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    dropdownRef.current?.moveSelection('up');
                    return true;
                }
                case 'ArrowDown': {
                    event.preventDefault();
                    dropdownRef.current?.moveSelection('down');
                    return true;
                }
                case 'Tab': {
                    event.preventDefault();
                    dismiss();
                    return true;
                }
                default: {
                    return false;
                }
            }
        },
        [state.isOpen, dismiss],
    );

    const insertReference = useCallback(
        (type: string, id: string, displayName: string) => {
            const editor = editorRef.current;
            if (!editor) {
                return;
            }

            const { triggerIndex } = state;
            if (triggerIndex < 0) {
                return;
            }

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) {
                return;
            }

            // Walk direct children of the editor to find the
            // text node containing the @ trigger. Pills count
            // as 1 char (matching getEditableTextAndCursor).
            const range = selection.getRangeAt(0);
            let offset = 0;
            let triggerNode: Text | null = null;
            let triggerNodeOffset = 0;

            for (const node of editor.childNodes) {
                if (node.nodeType === Node.TEXT_NODE) {
                    const len = node.textContent?.length ?? 0;
                    if (triggerNode === null && offset + len > triggerIndex) {
                        triggerNode = node as Text;
                        triggerNodeOffset = triggerIndex - offset;
                    }
                    offset += len;
                } else if (
                    node instanceof HTMLElement &&
                    node.dataset['refType']
                ) {
                    // Pill — count as 1 char
                    offset += 1;
                } else if (
                    node instanceof HTMLElement &&
                    node.tagName === 'BR'
                ) {
                    offset += 1;
                }
            }

            if (!triggerNode) {
                return;
            }

            // Replace from the @ trigger to the cursor with a mention pill.
            const deleteRange = document.createRange();
            deleteRange.setStart(triggerNode, triggerNodeOffset);
            deleteRange.setEnd(range.startContainer, range.startOffset);
            replaceRangeWithMentionPill(deleteRange, type, id, displayName);

            setState(CLOSED_STATE);
            onContentChange(serializeEditorContent(editor));
            editor.focus();
        },
        [editorRef, state, onContentChange],
    );

    const clearEditor = useCallback(() => {
        const editor = editorRef.current;
        if (editor) {
            editor.innerHTML = '';
        }
        setState(CLOSED_STATE);
    }, [editorRef]);

    return {
        isOpen: state.isOpen,
        query: state.query,
        handleInput,
        handleKeyDown,
        insertReference,
        dismiss,
        clearEditor,
        dropdownRef,
    };
}
